import React from 'react';
import { Link } from 'react-router-dom';
import GenericHelmet from '../../components/genericHelmet/GenericHelmet';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import './localOpsHowItWorks.css';

const HOW_IT_WORKS_URL = 'https://www.joegalaxy.net/localops/how-it-works';
const JG_LOCALOPS_SHARE_IMAGE =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png';

const camdenRoute = '/localops/region/camden?src=how-it-works&source=how_it_works';
const postTaskReturnTo =
  '/playermap?intent=post_task&regionSlug=camden&marketSlug=camden&source=how_it_works';
const postTaskRoute = `/localops/continue?returnTo=${encodeURIComponent(
  postTaskReturnTo,
)}`;

const paidTaskSteps = [
  {
    title: 'Post a local need',
    copy:
      'Start with something simple: a food pickup, grocery pickup, errand, cleaning help, home help, yard work, or small local job.',
  },
  {
    title: 'Fund the task',
    copy:
      'Paid tasks are funded before a helper accepts. The payment is held while the task is open.',
  },
  {
    title: 'A local helper completes the work',
    copy:
      'A nearby helper accepts the task, does the work, and updates the task in JG LocalOps.',
  },
  {
    title: 'Approve only when satisfied',
    copy:
      'Only approve completion once you are satisfied. Approval is what releases payout to the helper.',
  },
  {
    title: 'Payout releases and Action Credit strengthens',
    copy:
      "After completion is verified, payout is released to the helper's JGK Balance. Verified completion strengthens the helper's Action Credit record.",
  },
];

const offeringExamples = [
  'Soccer training',
  'Local food or products',
  'Handyman or lawn services',
  'Cleaning help',
  'Local event or pop-up services',
];

const faqItems = [
  {
    question: 'Are helpers vetted?',
    answer:
      'JG LocalOps starts with verified local action. Helpers build trust through completed tasks, requester verification, and Action Credit. Stronger helper levels for higher-trust work like rides and home access are part of the direction as LocalOps grows.',
  },
  {
    question: 'What if I am not satisfied?',
    answer:
      'Do not approve completion until you are satisfied. Completion approval is what releases payout to the helper.',
  },
  {
    question: 'How do helpers get paid?',
    answer:
      "Paid tasks are funded in JG LocalOps. After the requester verifies completion, payout is released to the helper's JGK Balance, and helpers can cash out through supported options.",
  },
  {
    question: 'What are Community Tasks?',
    answer:
      'Community Tasks are no-cash volunteer-style tasks through trusted local/community partners. Verified completion can still strengthen Action Credit.',
  },
  {
    question: 'Can businesses use this?',
    answer:
      'Yes. Local businesses can publish offerings, post local tasks, or use JG LocalOps to coordinate small local needs.',
  },
  {
    question: 'Is this only for Camden?',
    answer:
      'Camden is home base and where JG LocalOps is being proven first. JG LocalOps was built here in Camden, with real local tasks, helpers, offerings, payouts, and community action.',
  },
];

export default function LocalOpsHowItWorks() {
  return (
    <main className="localops-how">
      <GenericHelmet
        title="How JG LocalOps works"
        description="Learn how JG LocalOps tasks, offerings, Community Tasks, payment, completion approval, and Action Credit work."
        canonicalUrl={HOW_IT_WORKS_URL}
        image={JG_LOCALOPS_SHARE_IMAGE}
        url={HOW_IT_WORKS_URL}
        ogTitle="How JG LocalOps works"
        ogDescription="Learn how JG LocalOps tasks, offerings, Community Tasks, payment, completion approval, and Action Credit work."
        ogUrl={HOW_IT_WORKS_URL}
        twitterCard="summary_large_image"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'How JG LocalOps works',
          url: HOW_IT_WORKS_URL,
          description:
            'Learn how JG LocalOps tasks, offerings, Community Tasks, payment, completion approval, and Action Credit work.',
        }}
      />

      <section className="localops-how__hero" aria-labelledby="localops-how-title">
        <div className="localops-how__hero-inner">
          <div className="localops-how__brand">
            <JGLocalOpsMark label="JG LocalOps" />
            <span>Camden first. Camden-built.</span>
          </div>
          <h1 id="localops-how-title">How JG LocalOps works</h1>
          <p className="localops-how__subtitle">
            Post local tasks, offer services, or earn helping nearby.
          </p>
          <p className="localops-how__intro">
            JG LocalOps helps local people get things done and helps nearby
            helpers earn by completing useful local work.
          </p>
          <div className="localops-how__trust-loop" aria-label="LocalOps trust loop">
            <span>Post a local need</span>
            <span>Fund the task</span>
            <span>Helper completes the work</span>
            <span>Approve only when satisfied</span>
            <span>Payout releases</span>
            <span>Action Credit strengthens</span>
          </div>
          <div className="localops-how__actions">
            <Link className="localops-how__cta" to={camdenRoute}>
              Start with JG LocalOps Camden
            </Link>
            <Link className="localops-how__secondary" to={postTaskRoute}>
              Post a task
            </Link>
          </div>
        </div>
      </section>

      <section className="localops-how__section" aria-labelledby="paid-tasks-heading">
        <div className="localops-how__section-header">
          <span className="localops-how__eyebrow">Paid tasks</span>
          <h2 id="paid-tasks-heading">How paid tasks work</h2>
        </div>
        <div className="localops-how__steps">
          {paidTaskSteps.map((step, index) => (
            <article className="localops-how__step" key={step.title}>
              <span className="localops-how__step-number">{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
        <aside className="localops-how__note" aria-label="Requester control note">
          <strong>You stay in control</strong>
          <p>
            For paid tasks, the requester approves completion only when
            satisfied. If something is wrong, do not approve yet.
          </p>
        </aside>
      </section>

      <section className="localops-how__section" aria-labelledby="offerings-heading">
        <div className="localops-how__section-header">
          <span className="localops-how__eyebrow">Offerings</span>
          <h2 id="offerings-heading">Local offerings</h2>
        </div>
        <p>
          Local sellers, service providers, food vendors, and businesses can
          publish offerings. People nearby can view, request, or reserve what
          they offer through JG LocalOps.
        </p>
        <ul className="localops-how__chips" aria-label="Offering examples">
          {offeringExamples.map((example) => (
            <li key={example}>{example}</li>
          ))}
        </ul>
      </section>

      <section className="localops-how__section" aria-labelledby="community-tasks-heading">
        <div className="localops-how__section-header">
          <span className="localops-how__eyebrow">Community Tasks</span>
          <h2 id="community-tasks-heading">Community Tasks</h2>
        </div>
        <p>
          Community Tasks are no-cash volunteer-style tasks through trusted
          local/community partners. There is no cash payout. Verified completion
          can still strengthen Action Credit.
        </p>
        <p>
          A shelter dog-walk task can be a Community Task. Shopping, driving,
          delivery, supply runs, and paid errands should usually be posted as
          paid tasks unless the work is explicitly donated.
        </p>
      </section>

      <section className="localops-how__section" aria-labelledby="action-credit-heading">
        <div className="localops-how__section-header">
          <span className="localops-how__eyebrow">Action Credit</span>
          <h2 id="action-credit-heading">What is Action Credit?</h2>
        </div>
        <p>
          Action Credit is a trust signal strengthened by verified local
          completions. It helps show who has completed real local action through
          JG LocalOps.
        </p>
        <p>
          Verified Helper means a helper has completed verified LocalOps work
          and completed the required helper profile steps.
        </p>
        <p>
          Ride Ready means a Verified Helper has completed private,
          self-attested ride-readiness steps. It does not mean JG LocalOps has
          run a background check or provides auto insurance.
        </p>
      </section>

      <section className="localops-how__section" aria-labelledby="higher-trust-heading">
        <div className="localops-how__section-header">
          <span className="localops-how__eyebrow">Higher-trust work</span>
          <h2 id="higher-trust-heading">Different tasks need different trust levels</h2>
        </div>
        <p>
          Some categories, like rides, house sitting, pets, keys, or
          unsupervised home access, may need stronger helper verification as JG
          LocalOps grows.
        </p>
      </section>

      <section className="localops-how__section" aria-labelledby="faq-heading">
        <div className="localops-how__section-header">
          <span className="localops-how__eyebrow">Questions</span>
          <h2 id="faq-heading">Common questions</h2>
        </div>
        <div className="localops-how__faq">
          {faqItems.map((item) => (
            <article className="localops-how__faq-item" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="localops-how__final" aria-label="Start with LocalOps">
        <h2>Start with one useful local action</h2>
        <p>
          Post a small local task, publish an offering, or help nearby through JG
          LocalOps Camden.
        </p>
        <Link className="localops-how__cta" to={camdenRoute}>
          Start with JG LocalOps Camden
        </Link>
      </section>
    </main>
  );
}
